import { LayoutSectionParentKey, QuestionSource } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { trpcClient } from "components/rpc/trpc";
import { NgGrid } from "components/shared/grid";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import { useGetLayoutContainerByKeyQuery } from "generated/graphql";
import React, { useCallback } from "react";
import { stableQueryOptions } from "util/requests";

type HomeCareLeadDetail = inferProcedureOutput<AppRouter["homeCareLead"]["get"]>;
type HomeCareLeadUpdateInput = Parameters<
    typeof trpcClient.homeCareLead.update.mutate
>[0];

export const HomeCareLeadIntakeTab: React.FC<{
    client: HomeCareLeadDetail;
    onAnswerCommitted: () => void;
}> = ({ client, onAnswerCommitted }) => {
    const column1Query = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.HomeCareLeadIntakeColumn1 },
        stableQueryOptions(),
    );
    const column2Query = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.HomeCareLeadIntakeColumn2 },
        stableQueryOptions(),
    );

    const handleFieldChange = useCallback(
        async (field: string, value: unknown) => {
            const patch = { id: client.id, [field]: value } as HomeCareLeadUpdateInput;
            await trpcClient.homeCareLead.update.mutate(patch);
            onAnswerCommitted();
        },
        [client.id, onAnswerCommitted],
    );

    if (column1Query.isLoading || column2Query.isLoading) {
        return <Spinner />;
    }

    const column1Sections =
        column1Query.data?.getLayoutContainerByKey?.layoutSections ?? [];
    const column2Sections =
        column2Query.data?.getLayoutContainerByKey?.layoutSections ?? [];

    if (column1Sections.length === 0 && column2Sections.length === 0) {
        return (
            <InlineBanner type="info" fontStyle="medium">
                Intake fields are not yet set up for this tenant. An administrator can run
                Ensure Home Care Setup from Tenant Settings to seed them.
            </InlineBanner>
        );
    }

    const renderSection = (
        section: (typeof column1Sections)[number],
    ): React.ReactElement => (
        <LayoutFormSection
            key={section.id}
            entityId={client.id}
            entity={client}
            entityType="HomeCareLead"
            layoutSection={section}
            mode="tile"
            answerMode={QuestionSource.Entity}
            onAnswerCommitted={onAnswerCommitted}
            onFieldChange={handleFieldChange}
        />
    );

    return (
        <NgGrid>
            <NgGrid.Column>{column1Sections.map(renderSection)}</NgGrid.Column>
            <NgGrid.Column>{column2Sections.map(renderSection)}</NgGrid.Column>
        </NgGrid>
    );
};
